var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import changePassword from 'root/src/client/logic/auth/thunks/changePassword';
import { PASSWORD_SCHEMA } from 'root/src/client/v2/auth/schemas/password.schema';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { PasswordInput } from 'root/src/client/v2/common/components/ui/password-input';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
const changePasswordSchema = z.object({
    password: z.string().min(1, 'Current password is required'),
    newPassword: PASSWORD_SCHEMA,
    confirmPassword: z.string().min(1, 'Confirm your new password'),
}).refine(data => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
});
export const ChangePasswordPanel = () => {
    var _a, _b, _c;
    const dispatch = useAppDispatch();
    const [failed, setFailed] = useState(false);
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(changePasswordSchema),
    });
    const onSubmit = handleSubmit(({ password, newPassword }) => __awaiter(void 0, void 0, void 0, function* () {
        setFailed(false);
        try {
            // On success the thunk signs the user out and redirects to login (with a success modal).
            yield dispatch(changePassword({ password, newPassword }));
        }
        catch (e) {
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
            const message = e instanceof Error ? e.message : 'Please check your current password and try again.';
            toast.error('Could not change password', { description: message });
        }
    }), () => {
        toast.error('Check the form', { description: 'Fix the highlighted fields.' });
    });
    return (<div>
			<h3 className="mb-1 text-sm font-semibold">Change Password</h3>
			<p className="mb-4 text-xs text-muted-foreground">You will be signed out after changing your password.</p>
			<form onSubmit={onSubmit} className="space-y-3">
				<Field label="Current password" htmlFor="current-password" error={(_a = errors.password) === null || _a === void 0 ? void 0 : _a.message}>
					<PasswordInput id="current-password" autoComplete="current-password" error={!!errors.password} {...register('password')}/>
				</Field>
				<Field label="New password" htmlFor="new-password" error={(_b = errors.newPassword) === null || _b === void 0 ? void 0 : _b.message}>
					<PasswordInput id="new-password" autoComplete="new-password" error={!!errors.newPassword} {...register('newPassword')}/>
					<p className="text-[10px] text-muted-foreground/60">At least 8 characters with an upper, lower, number and symbol.</p>
				</Field>
				<Field label="Confirm new password" htmlFor="confirm-password" error={(_c = errors.confirmPassword) === null || _c === void 0 ? void 0 : _c.message}>
					<PasswordInput id="confirm-password" autoComplete="new-password" error={!!errors.confirmPassword} {...register('confirmPassword')}/>
				</Field>
				<div className="flex justify-end">
					<Button type="submit" size="sm" loading={isSubmitting} error={failed} className="h-8 min-w-[120px] rounded-lg text-[11px]">
						Change password
					</Button>
				</div>
			</form>
		</div>);
};
